// MapSection.jsx – Basemaps + product raster overlay + map tools + farm polygons (filterable)
import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapSection.css";
import BaseMapToggle from "./BaseMapToggle";
import Legend from "./Legend";
import MapButtons from "./MapButtons";
import BottomChartPanel from "./BottomChartPanel";
import { getProductTileUrl } from "../api/productsApi";
import LocationPinCursorRed from "../assets/icons/location_pin_red_40.svg";

// Custom marker icon for polygon vertices
const vertexIcon = L.divIcon({
  className: "polygon-vertex-marker",
  html: "●",
  iconSize: [12, 12],
  popupAnchor: [0, -6],
});

const osmLayer = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
  maxZoom: 19,
  attribution:
    '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
});

const googleSatellite = L.tileLayer(
  "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
  {
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
);

const arcgisImagery = L.tileLayer(
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  {
    maxZoom: 19,
    attribution:
      "&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
  },
);

const baseMaps = {
  osm: osmLayer,
  satellite: googleSatellite,
  imagery: arcgisImagery,
};

const MapSection = ({
  isSideMenuOpen,
  isBottomMenuOpen,
  activeIndicator,
  selectedProductId,
  selectedProductType,
  productConfig,
  legendData,
  visualizationParams,
  activeTool,
  onPixelSelect,
  setActiveTool,
  onRequestTimeFilter,
  onOpenTimeFilter,
  timeSeriesData,
  timeSeriesError,
  filteredPolygon,   // GeoJSON FeatureCollection or null
  onLogout,          // callback to log out from App
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const activeBaseLayerRef = useRef(null);
  const productLayerRef = useRef(null);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [isBottomChartExpanded, setIsBottomChartExpanded] = useState(true);
  const [baseMap, setBaseMap] = useState("satellite");
  const polygonLayerRef = useRef(null);            // user‑drawn polygon
  const activeToolRef = useRef(null);
  const vertexMarkersRef = useRef([]);
  const polygonPointsRef = useRef([]);
  const farmsLoadedRef = useRef(false);
  const allFarmsGroupRef = useRef(null);           // LayerGroup for all user farms
  const filteredPolygonLayerRef = useRef(null);    // single filtered polygon
  const selectedFarmLayerRef = useRef(null);       // currently selected farm highlight
  const [weatherCenter, setWeatherCenter] = useState(null);   // centroid for weather
  const [farmsLoading, setFarmsLoading] = useState(false);    // non-blocking loading toast
  const [farmsError, setFarmsError] = useState(null);         // farms fetch error
  const [hoveredFarm, setHoveredFarm] = useState(null);       // { farm_code, crop_type, area, ... }
  const [selectedFarm, setSelectedFarm] = useState(null);     // currently selected farm
  const [farmContextMenu, setFarmContextMenu] = useState({ visible: false, x: 0, y: 0, farm: null, layer: null });

  const toggleLegend = () => setIsLegendOpen(!isLegendOpen);

  useEffect(() => {
    setIsLegendOpen(false);
  }, [activeIndicator]);

  useEffect(() => {
    // keep a ref to the latest activeTool so event handlers can check it
    activeToolRef.current = activeTool;
  }, [activeTool]);


  useEffect(() => {
    if (mapInstanceRef.current || !mapContainerRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [31.5, 48.5],
      zoom: 10,
      zoomControl: false,
      doubleClickZoom: false,
    });

    googleSatellite.addTo(map);
    activeBaseLayerRef.current = googleSatellite;
    mapInstanceRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);

    // Create an empty LayerGroup that will hold all farm polygons
    const group = L.layerGroup();
    group.addTo(map);
    allFarmsGroupRef.current = group;

    return () => {
      try {
        map.remove();
      } catch (e) {
        /* ignore */
      }
      mapInstanceRef.current = null;
    };
  }, []);

  // ------------------------------------------------------------------
  // LOAD USER'S FARM POLYGONS AFTER LOGIN (once)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!mapInstanceRef.current || farmsLoadedRef.current) return;
    const token = localStorage.getItem("authToken");
    if (!token) return;

    const apiBase =
      process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
      "http://178.131.60.38:8000/api";

    const fetchFarms = async () => {
      setFarmsLoading(true);
      setFarmsError(null);
      try {
        const response = await fetch(`${apiBase}/farms/list_with_polygons/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch farms");
        const data = await response.json();
        const farms = data.farms || [];
        const map = mapInstanceRef.current;
        const group = allFarmsGroupRef.current;
        const bounds = L.latLngBounds([]);
        let hasAny = false;

        farms.forEach((farm) => {
          const polygon = farm.farm_polygon;
          if (!polygon || polygon.type !== "Polygon") return;

          const latLngs = polygon.coordinates[0].map(([lng, lat]) => [lat, lng]);

          // Build a display label from available fields
          const farmLabel =
            farm.farm_code ||
            farm.block_code ||
            farm.office ||
            farm.management ||
            `مزرعه ${farm.id}`;

          const farmMeta = {
            id: farm.id,
            label: farmLabel,
            farm_code: farm.farm_code,
            block_code: farm.block_code,
            office: farm.office,
            management: farm.management,
            crop_type: farm.crop_type,
            area: farm.area,
            status: farm.status,
          };

          const layer = L.polygon(latLngs, {
            color: "#ffa500",
            weight: 2,
            fillOpacity: 0,
            className: "farm-polygon",
          });

          // Hover: show info box (disabled while drawing/selecting tools are active)
          layer.on("mouseover", () => {
            if (activeToolRef.current === "pixelSelect" || activeToolRef.current === "polygonSelect") return;
            setHoveredFarm(farmMeta);
            layer.setStyle({ color: "#ff6600", weight: 3, fillColor: "#ffa500", fillOpacity: 0.15 });
          });
          layer.on("mouseout", () => {
            if (activeToolRef.current === "pixelSelect" || activeToolRef.current === "polygonSelect") return;
            setHoveredFarm(null);
            // Only reset style if this farm isn't the selected one
            if (!layer._isSelected) {
              layer.setStyle({ color: "#ffa500", weight: 2, fillOpacity: 0 });
            }
          });

          // Click: select farm and trigger time-series flow (ignored while tools are active)
          layer.on("click", (e) => {
            if (activeToolRef.current === "pixelSelect" || activeToolRef.current === "polygonSelect") return;
            L.DomEvent.stopPropagation(e);

            // Deselect previously selected layer
            if (selectedFarmLayerRef.current && selectedFarmLayerRef.current !== layer) {
              selectedFarmLayerRef.current._isSelected = false;
              selectedFarmLayerRef.current.setStyle({ color: "#ffa500", weight: 2, fillOpacity: 0 });
            }

            layer._isSelected = true;
            selectedFarmLayerRef.current = layer;
            setSelectedFarm(farmMeta);

            layer.setStyle({ color: "#2c7a47", weight: 3, fillColor: "#2c7a47", fillOpacity: 0.15 });

            // Feed coordinates into the time-series selection system
            const coords = latLngs.map(([lat, lng]) => [lng, lat]);
            onPixelSelect?.({ type: "polygon", coordinates: coords });
            onRequestTimeFilter?.();
          });

          // Context menu (right-click): show small action menu
          layer.on("contextmenu", (e) => {
            // ignore when drawing/selecting
            if (activeToolRef.current === "pixelSelect" || activeToolRef.current === "polygonSelect") return;
            // prevent browser menu
            if (e.originalEvent && typeof e.originalEvent.preventDefault === "function") {
              e.originalEvent.preventDefault();
            }
            L.DomEvent.stopPropagation(e);
            const clientX = e.originalEvent ? e.originalEvent.clientX : 0;
            const clientY = e.originalEvent ? e.originalEvent.clientY : 0;
            setFarmContextMenu({ visible: true, x: clientX, y: clientY, farm: farmMeta, layer });
          });

          layer.addTo(group);
          bounds.extend(latLngs);
          hasAny = true;
        });

        if (hasAny) {
          map.fitBounds(bounds, { padding: [50, 50] });
          const center = bounds.getCenter();
          setWeatherCenter({ lat: center.lat, lng: center.lng });
        }
      } catch (err) {
        console.error("Error loading farm polygons:", err);
        setFarmsError("بارگذاری مزارع ناموفق بود");
        // Auto-clear error after 4 s
        setTimeout(() => setFarmsError(null), 4000);
      } finally {
        setFarmsLoading(false);
      }
    };

    fetchFarms();
    farmsLoadedRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstanceRef.current]);

  // ------------------------------------------------------------------
  // HANDLE FILTERED POLYGON (show/hide all farms accordingly)
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove any previously displayed filtered polygon
    if (filteredPolygonLayerRef.current) {
      map.removeLayer(filteredPolygonLayerRef.current);
      filteredPolygonLayerRef.current = null;
    }

    if (filteredPolygon) {
      // Hide the all‑farms layer
      if (allFarmsGroupRef.current && map.hasLayer(allFarmsGroupRef.current)) {
        map.removeLayer(allFarmsGroupRef.current);
      }

      // Add the new filtered polygon(s) – border only
      const geoLayer = L.geoJSON(filteredPolygon, {
        style: {
          color: "#2c7a47",
          weight: 4,
          fillColor: "transparent",
          fillOpacity: 0,
        },
      });
      geoLayer.addTo(map);
      filteredPolygonLayerRef.current = geoLayer;

      // Zoom to the filtered polygon
      const bounds = geoLayer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    } else {
      // filteredPolygon is null – restore the all‑farms layer
      if (
        allFarmsGroupRef.current &&
        !map.hasLayer(allFarmsGroupRef.current)
      ) {
        allFarmsGroupRef.current.addTo(map);
      }
    }
  }, [filteredPolygon]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (activeBaseLayerRef.current) {
      map.removeLayer(activeBaseLayerRef.current);
    }
    const newLayer = baseMaps[baseMap] || googleSatellite;
    newLayer.addTo(map);
    activeBaseLayerRef.current = newLayer;
  }, [baseMap]);

  // Product raster overlay
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (productLayerRef.current) {
      map.removeLayer(productLayerRef.current);
      productLayerRef.current = null;
    }

    if (!selectedProductId || !activeIndicator) return;

    const min = productConfig?.minValue ?? -1;
    const max = productConfig?.maxValue ?? 1;
    const opacity = visualizationParams?.opacity ?? 0.85;

    const tileUrl = getProductTileUrl(selectedProductId, { min, max });
    const layer = L.tileLayer(tileUrl, {
      maxZoom: 20,
      opacity,
      zIndex: 10,
    });

    layer.addTo(map);
    productLayerRef.current = layer;

    return () => {
      if (productLayerRef.current && map.hasLayer(productLayerRef.current)) {
        map.removeLayer(productLayerRef.current);
      }
      productLayerRef.current = null;
    };
  }, [selectedProductId, activeIndicator, productConfig, visualizationParams]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      setTimeout(() => mapInstanceRef.current.invalidateSize(), 300);
    }
  }, [isSideMenuOpen, isBottomMenuOpen, isBottomChartExpanded]);

  const hasBottomChartPanel =
    isBottomMenuOpen && timeSeriesData && timeSeriesData.length > 0;

  // Cursor for pixel select
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const container = map.getContainer();
    if (!container) return;

    const setCursorImportant = (el, value) => {
      try {
        el.style.setProperty("cursor", value, "important");
      } catch (_) {
        el.style.cursor = value;
      }
    };
    const clearCursor = (el) => {
      try {
        el.style.removeProperty("cursor");
      } catch (_) {
        el.style.cursor = "";
      }
    };

    const panes = container.querySelectorAll(
      ".leaflet-container, .leaflet-pane, .leaflet-map-pane, .leaflet-tile-pane, .leaflet-objects-pane, .leaflet-overlay-pane, .leaflet-shadow-pane, .leaflet-marker-pane, .leaflet-popup-pane",
    );

    if (activeTool === "pixelSelect") {
      const cursorValue = `url(${LocationPinCursorRed}) 20 39, crosshair`;
      setCursorImportant(container, cursorValue);
      panes.forEach((p) => setCursorImportant(p, cursorValue));
    } else {
      clearCursor(container);
      panes.forEach((p) => clearCursor(p));
    }

    return () => {
      clearCursor(container);
      panes.forEach((p) => clearCursor(p));
    };
  }, [activeTool]);

  // ------------------------------------------------------------------
  // POLYGON DRAWING LOGIC (unchanged)
  // ------------------------------------------------------------------
  const clearPolygonDrawing = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (polygonLayerRef.current) {
      map.removeLayer(polygonLayerRef.current);
      polygonLayerRef.current = null;
    }
    vertexMarkersRef.current.forEach((marker) => {
      if (map.hasLayer(marker)) map.removeLayer(marker);
    });
    vertexMarkersRef.current = [];
    polygonPointsRef.current = [];
  }, []);

  const updatePolygonDisplay = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (polygonLayerRef.current) map.removeLayer(polygonLayerRef.current);
    vertexMarkersRef.current.forEach((m) => map.removeLayer(m));
    vertexMarkersRef.current = [];

    const points = polygonPointsRef.current;
    if (points.length === 0) return;

    if (points.length >= 3) {
      polygonLayerRef.current = L.polygon(points, {
        color: "#2c7a47",
        weight: 3,
        fillColor: "#4caf50",
        fillOpacity: 0.15,
        dashArray: "5, 5",
        lineJoin: "round",
      }).addTo(map);
    } else {
      polygonLayerRef.current = L.polyline(points, {
        color: "#2c7a47",
        weight: 4,
        dashArray: "8, 6",
        lineJoin: "round",
      }).addTo(map);
    }

    points.forEach((point, idx) => {
      const marker = L.marker(point, {
        icon: vertexIcon,
        draggable: false,
        zIndexOffset: 1000,
      }).addTo(map);
      marker.bindTooltip(`${idx + 1}`, { permanent: false, direction: "top" });
      vertexMarkersRef.current.push(marker);
    });
  }, []);

  const finishPolygon = useCallback(() => {
    const points = polygonPointsRef.current;
    if (points.length < 3) {
      clearPolygonDrawing();
      setActiveTool?.(null);
      return;
    }
    const coordinates = points.map(([lat, lng]) => [lng, lat]);
    if (coordinates.length >= 3) {
      const first = coordinates[0];
      const last = coordinates[coordinates.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coordinates.push(first);
      }
    }
    onPixelSelect?.({ type: "polygon", coordinates });
    clearPolygonDrawing();
    setActiveTool?.(null);
    onRequestTimeFilter?.();
  }, [onPixelSelect, setActiveTool, onRequestTimeFilter, clearPolygonDrawing]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const handleMapClick = (e) => {
      const { lat, lng } = e.latlng;

      if (activeTool === "pixelSelect") {
        onPixelSelect?.({ type: "point", lat, lon: lng });
        setActiveTool?.(null);
        onRequestTimeFilter?.();
      }

      if (activeTool === "polygonSelect") {
        polygonPointsRef.current.push([lat, lng]);
        updatePolygonDisplay();
      }
    };

    const handleMapDoubleClick = (e) => {
      if (activeTool === "polygonSelect") {
        e.originalEvent.preventDefault();
        finishPolygon();
      }
    };

    const handleKeyDown = (e) => {
      if (activeTool === "polygonSelect" && e.key === "Enter") {
        finishPolygon();
      }
    };

    if (activeTool === "pixelSelect" || activeTool === "polygonSelect") {
      map.on("click", handleMapClick);
    }
    if (activeTool === "polygonSelect") {
      map.on("dblclick", handleMapDoubleClick);
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      map.off("click", handleMapClick);
      map.off("dblclick", handleMapDoubleClick);
      window.removeEventListener("keydown", handleKeyDown);
      if (activeTool !== "polygonSelect") {
        clearPolygonDrawing();
      }
    };
  }, [
    activeTool,
    onPixelSelect,
    setActiveTool,
    onRequestTimeFilter,
    updatePolygonDisplay,
    finishPolygon,
    clearPolygonDrawing,
  ]);

  useEffect(() => {
    if (activeTool !== "polygonSelect") {
      clearPolygonDrawing();
    }
  }, [activeTool, clearPolygonDrawing]);

  // Hide farm context menu on map click or Escape
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (farmContextMenu.visible) setFarmContextMenu((s) => ({ ...s, visible: false }));
    };
    const handleEsc = (e) => {
      if (e.key === "Escape" && farmContextMenu.visible) setFarmContextMenu((s) => ({ ...s, visible: false }));
    };
    window.addEventListener("mousedown", handleGlobalClick);
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("mousedown", handleGlobalClick);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [farmContextMenu.visible]);

  // Prevent the browser/map context menu when tools that capture clicks are active
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const container = map.getContainer();
    if (!container) return;

    const handler = (e) => {
      if (activeToolRef.current === "pixelSelect" || activeToolRef.current === "polygonSelect") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    container.addEventListener("contextmenu", handler);
    return () => container.removeEventListener("contextmenu", handler);
  }, [mapInstanceRef.current]);

  // Helper: extract [lng, lat] coordinates from a Leaflet polygon/polygon-layer
  const extractLayerCoordinates = (layer) => {
    try {
      if (!layer || !layer.getLatLngs) return [];
      const latlngs = layer.getLatLngs();
      // latlngs may be nested arrays (rings) for polygons/multipolygons
      const ring = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
      if (!Array.isArray(ring)) return [];
      return ring.map((pt) => [pt.lng, pt.lat]);
    } catch (err) {
      return [];
    }
  };

  // Bottom offset for controls when chart is open
  const controlsBottom = hasBottomChartPanel && isBottomChartExpanded ? 355 : 20;

  return (
    <div
      className={`map-section ${activeTool === "pixelSelect" ? "pixel-select-active" : ""} ${
        isSideMenuOpen ? "side-menu-open" : "side-menu-closed"
      } ${isBottomMenuOpen ? "bottom-menu-open" : "bottom-menu-closed"} ${
        hasBottomChartPanel
          ? isBottomChartExpanded
            ? "bottom-chart-expanded"
            : "bottom-chart-collapsed"
          : ""
      }`}
    >
      <div
        ref={mapContainerRef}
        className="leaflet-map"
        style={{ width: "100%", height: "100%", zIndex: 1 }}
      />

      {/* Legend – positioned on the left (its own CSS handles absolute positioning) */}
      {activeIndicator && (
        <Legend
          isOpen={isLegendOpen}
          toggleLegend={toggleLegend}
          hasBottomChart={hasBottomChartPanel && isBottomChartExpanded}
          activeProductType={selectedProductType}
          legendData={legendData}
          productConfig={productConfig}
          steps={10}
        />
      )}

      {/* Basemap toggle – positioned on the bottom right */}
      <div
        style={{
          position: "absolute",
          bottom: `${controlsBottom}px`,
          right: "20px",
          zIndex: 500,
        }}
      >
        <BaseMapToggle currentBaseMap={baseMap} setBaseMap={setBaseMap} />
      </div>

      {/* ── Farm loading toast (non-blocking) ─────────────────────── */}
      {farmsLoading && (
        <div className="farm-loading-toast" role="status" aria-live="polite">
          <span className="farm-loading-spinner" />
          در حال بارگذاری مزارع...
        </div>
      )}

      {/* ── Farm fetch error toast ────────────────────────────────── */}
      {farmsError && (
        <div className="farm-error-toast" role="alert">
          {farmsError}
        </div>
      )}

      {/* ── Farm hover info box (top-right) ──────────────────────── */}
      {hoveredFarm && (
        <div className="farm-info-box" dir="rtl">
          <div className="farm-info-title">{hoveredFarm.label}</div>
          <div className="farm-info-rows">
            {hoveredFarm.farm_code && (
              <div className="farm-info-row">
                <span className="farm-info-key">کد مزرعه</span>
                <span className="farm-info-value">{hoveredFarm.farm_code}</span>
              </div>
            )}
            {hoveredFarm.block_code && (
              <div className="farm-info-row">
                <span className="farm-info-key">کد بلوک</span>
                <span className="farm-info-value">{hoveredFarm.block_code}</span>
              </div>
            )}
            {hoveredFarm.office && (
              <div className="farm-info-row">
                <span className="farm-info-key">اداره</span>
                <span className="farm-info-value">{hoveredFarm.office}</span>
              </div>
            )}
            {hoveredFarm.management && (
              <div className="farm-info-row">
                <span className="farm-info-key">مدیریت</span>
                <span className="farm-info-value">{hoveredFarm.management}</span>
              </div>
            )}
            {hoveredFarm.crop_type && (
              <div className="farm-info-row">
                <span className="farm-info-key">محصول</span>
                <span className="farm-info-value">{hoveredFarm.crop_type}</span>
              </div>
            )}
            {hoveredFarm.area != null && (
              <div className="farm-info-row">
                <span className="farm-info-key">مساحت</span>
                <span className="farm-info-value">{hoveredFarm.area} هکتار</span>
              </div>
            )}
            {hoveredFarm.status && (
              <div className="farm-info-row">
                <span className="farm-info-key">وضعیت</span>
                <span className="farm-info-value">{hoveredFarm.status}</span>
              </div>
            )}
          </div>
          <div className="farm-info-hint">کلیک کنید تا انتخاب شود</div>
        </div>
      )}

      {/* ── Selected farm badge ───────────────────────────────────── */}
      {selectedFarm && !hoveredFarm && (
        <div className="farm-selected-badge" dir="rtl">
          <span>✓</span>
          <span>{selectedFarm.label} انتخاب شد</span>
          <button
            className="farm-badge-clear"
            onClick={() => {
              setSelectedFarm(null);
              // Reset all farm polygon styles
              if (selectedFarmLayerRef.current) {
                selectedFarmLayerRef.current._isSelected = false;
                selectedFarmLayerRef.current.setStyle({ color: "#ffa500", weight: 2, fillOpacity: 0 });
                selectedFarmLayerRef.current = null;
              }
            }}
          >
            ×
          </button>
        </div>
      )}

      <MapButtons
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        mapRef={mapInstanceRef}
        weatherCenter={weatherCenter}
        onLogout={onLogout}
      />

      {/* Farm context menu (right-click) */}
      {farmContextMenu.visible && (
        <div
          className="farm-context-menu"
          style={{
            position: "fixed",
            left: farmContextMenu.x,
            top: farmContextMenu.y,
            zIndex: 12000,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 6,
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            padding: "6px 8px",
            minWidth: 160,
            direction: "rtl",
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13 }}>{farmContextMenu.farm?.label || "مزرعه"}</div>
            <button
              onClick={() => setFarmContextMenu((s) => ({ ...s, visible: false }))}
              aria-label="بستن"
              style={{ border: "none", background: "transparent", cursor: "pointer" }}
            >
              ×
            </button>
          </div>
          <div style={{ height: 8 }} />
          <button
            onClick={() => {
              const { layer, farm } = farmContextMenu;
              if (layer) {
                // Deselect previous
                if (selectedFarmLayerRef.current && selectedFarmLayerRef.current !== layer) {
                  selectedFarmLayerRef.current._isSelected = false;
                  selectedFarmLayerRef.current.setStyle({ color: "#ffa500", weight: 2, fillOpacity: 0 });
                }
                layer._isSelected = true;
                selectedFarmLayerRef.current = layer;
                setSelectedFarm(farm);
                layer.setStyle({ color: "#2c7a47", weight: 3, fillColor: "#2c7a47", fillOpacity: 0.15 });

                // prepare coordinates and trigger time-series flow
                const coords = extractLayerCoordinates(layer);
                onPixelSelect?.({ type: "polygon", coordinates: coords });
                onRequestTimeFilter?.();
              }
              setFarmContextMenu((s) => ({ ...s, visible: false }));
            }}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 6,
              border: "none",
              background: "#2c7a47",
              color: "#fff",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            ایجاد سری زمانی برای این مزرعه
          </button>
        </div>
      )}

      {activeTool === "polygonSelect" &&
        polygonPointsRef.current.length >= 3 && (
          <div className="polygon-finish-btn" onClick={finishPolygon}>
            ✓ پایان رسم چندضلعی
          </div>
        )}

      {hasBottomChartPanel && (
        <BottomChartPanel
          data={timeSeriesData}
          productConfig={productConfig}
          activeIndicator={activeIndicator}
          onExpandChange={setIsBottomChartExpanded}
          onOpenTimeFilter={onOpenTimeFilter}
        />
      )}

      {timeSeriesError && isBottomMenuOpen && !hasBottomChartPanel && (
        <div className="map-chart-error-hint">{timeSeriesError}</div>
      )}
    </div>
  );
};

export default MapSection;